import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import {
  buildPostTaskPath,
  buildPublicProofUrl,
  buildReceiptPath,
  formatAmount,
  formatDuration,
  getBackendOrigin,
  getPlayerDisplay,
  getProofTitle,
  normalizeProofMediaList,
  resolveCoverIndex,
  safeText,
} from './proofPortfolioUtils';
import {
  getMediaThumbnailUrl,
} from '../../utils/postMediaUtils';
import './localOpsProofPortfolio.css';

function ProofMediaPreview({ proof }) {
  const mediaList = useMemo(() => normalizeProofMediaList(proof), [proof]);
  const media = mediaList[resolveCoverIndex(proof, mediaList)] || null;
  if (!media?.url) {
    return (
      <div className="localops-proof-portfolio-card__media-fallback">
        <span>Verified proof</span>
      </div>
    );
  }
  if (media.type === 'video') {
    const posterUrl = media.thumbnailUrl || media.posterUrl || getMediaThumbnailUrl(media);
    if (posterUrl) {
      return (
        <img
          className="localops-proof-portfolio-card__media"
          src={posterUrl}
          alt=""
        />
      );
    }
    return (
      <video
        className="localops-proof-portfolio-card__media"
        src={media.url}
        controls
        playsInline
        preload="metadata"
      />
    );
  }
  return (
    <img
      className="localops-proof-portfolio-card__media"
      src={media.url}
      alt=""
    />
  );
}

function ProofCard({ proof, onCopy, copied }) {
  const receiptPath = buildReceiptPath(proof);
  const completedDuration = formatDuration(
    proof?.completedInSeconds ?? proof?.velocity?.completedInSeconds,
  );
  const amount = formatAmount(proof);

  return (
    <article className="localops-proof-portfolio-card">
      <ProofMediaPreview proof={proof} />
      <div className="localops-proof-portfolio-card__body">
        <div className="localops-proof-portfolio-card__eyebrow">
          JG LocalOps completion
        </div>
        <h2>{getProofTitle(proof)}</h2>
        <div className="localops-proof-portfolio-card__facts">
          <span>{safeText(proof?.regionLabel, 'Local area')}</span>
          {completedDuration ? <span>Completed in {completedDuration}</span> : null}
          {amount ? <span>{amount}</span> : null}
        </div>
        <div className="localops-proof-portfolio-card__actions">
          {receiptPath ? (
            <Link
              className="localops-proof-portfolio-link"
              to={receiptPath}
            >
              View Action Receipt
            </Link>
          ) : null}
          <button
            className="localops-proof-portfolio-button"
            type="button"
            onClick={() => onCopy(proof)}
          >
            {copied ? 'Copied' : 'Copy Proof Link'}
          </button>
          <a
            className="localops-proof-portfolio-cta"
            href={buildPostTaskPath(proof)}
          >
            Post a Local Task
          </a>
        </div>
      </div>
    </article>
  );
}

export default function LocalOpsProofPortfolio() {
  const { playerId } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copiedProofId, setCopiedProofId] = useState('');

  useEffect(() => {
    const prevBodyBg = document.body.style.background;
    const prevHtmlBg = document.documentElement.style.background;
    document.body.style.background = '#050912';
    document.documentElement.style.background = '#050912';
    return () => {
      document.body.style.background = prevBodyBg;
      document.documentElement.style.background = prevHtmlBg;
    };
  }, []);

  useEffect(() => {
    if (!playerId) {
      setLoading(false);
      setNotFound(true);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    fetch(
      `${getBackendOrigin()}/api/v1/local-ops/players/${encodeURIComponent(
        playerId,
      )}/proofs`,
      { headers: { Accept: 'application/json' } },
    )
      .then((res) => {
        if (!res.ok) throw new Error('not-found');
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        setPortfolio(json?.data || null);
        setNotFound(!json?.data);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [playerId]);

  const player = portfolio?.player || {};
  const proofs = Array.isArray(portfolio?.proofs) ? portfolio.proofs : [];
  const playerDisplay = getPlayerDisplay(player);

  const handleCopy = async (proof) => {
    const publicUrl = buildPublicProofUrl(proof);
    if (!publicUrl) return;
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedProofId(proof?.proofId || publicUrl);
      window.setTimeout(() => {
        setCopiedProofId((current) =>
          current === (proof?.proofId || publicUrl) ? '' : current,
        );
      }, 1800);
    } catch (err) {
      setCopiedProofId('');
    }
  };

  if (loading) {
    return (
      <main className="localops-proof-portfolio-page">
        <section className="localops-proof-portfolio-shell localops-proof-portfolio-shell--loading">
          <JGLocalOpsMark className="localops-proof-portfolio-mark" />
          <p>Loading proof portfolio...</p>
        </section>
      </main>
    );
  }

  if (notFound || !portfolio) {
    return (
      <main className="localops-proof-portfolio-page">
        <section className="localops-proof-portfolio-shell localops-proof-portfolio-shell--empty">
          <JGLocalOpsMark className="localops-proof-portfolio-mark" />
          <p className="localops-proof-portfolio-kicker">LocalOps proofs</p>
          <h1>Proof portfolio unavailable</h1>
          <p>This public proof portfolio is not available.</p>
          <a
            className="localops-proof-portfolio-hero-cta"
            href="/playermap?intent=post_task&source=proof_portfolio_empty"
          >
            Post a Local Task
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="localops-proof-portfolio-page">
      <section
        className="localops-proof-portfolio-shell"
        data-testid="localops-proof-portfolio"
      >
        <JGLocalOpsMark className="localops-proof-portfolio-mark" />
        <header className="localops-proof-portfolio-header">
          <div>
            <p className="localops-proof-portfolio-kicker">LocalOps Proof Portfolio</p>
            <h1>{playerDisplay}</h1>
            <p>Verified Action Proof Receipts for completed LocalOps tasks.</p>
          </div>
          {player.profilePhoto ? (
            <img
              className="localops-proof-portfolio-avatar"
              src={player.profilePhoto}
              alt=""
            />
          ) : null}
        </header>

        {proofs.length ? (
          <div className="localops-proof-portfolio-grid">
            {proofs.map((proof) => (
              <ProofCard
                key={proof.proofId || proof.sharePath}
                proof={proof}
                onCopy={handleCopy}
                copied={copiedProofId === (proof.proofId || buildPublicProofUrl(proof))}
              />
            ))}
          </div>
        ) : (
          <section className="localops-proof-portfolio-empty">
            <h2>No verified LocalOps proofs yet</h2>
            <p>Completed LocalOps tasks will appear here after verification.</p>
            <a
              className="localops-proof-portfolio-hero-cta"
              href="/playermap?intent=post_task&source=proof_portfolio_empty"
            >
              Post a Local Task
            </a>
          </section>
        )}
      </section>
    </main>
  );
}
